/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, Input, inject } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import {
  IBoton,
  ICategoria,
  IReemplazoCategoriaCliente,
  IUsoCategoriaEnCliente,
} from 'modelos/src';
import {
  BOTONERA_SPECS,
  BotoneraTemplateId,
} from '../../../../auxiliares/plantillas/plantillas-spec';
import {
  TemplateCustom,
  templateCustomDeCliente,
} from '../../../../auxiliares/plantillas/templates-custom';
import { DialogService } from '../../../../auxiliares/dialog/dialog.service';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import {
  AgregarCategoriaDialogComponent,
  DatosAgregarCategoria,
  ResultadoAgregarCategoria,
} from '../agregar-categoria-dialog/agregar-categoria-dialog.component';
import {
  IDatosReemplazoDialog,
  IFilaReemplazo,
  ReemplazoCategoriaDialogComponent,
  ResultadoReemplazo,
} from '../../../categorias/reemplazo-categoria-dialog/reemplazo-categoria-dialog.component';
import { ID_PLANTILLA_MANUAL } from '../../../../auxiliares/plantillas/plantilla-manual';
import { CategoriaEditable, etiquetaEditable } from '../cliente-form.model';
import { ClienteFormService } from '../cliente-form.service';

/**
 * Sección "Categorías" del form unificado: la ÚNICA pantalla donde se eligen, crean y editan las
 * categorías de un cliente, más la asignación al vecino (default y por edad).
 *
 * Reemplaza `categorias-cliente` (tab General de `crear-editar-cliente`) + el bloque de categorías
 * de `tab-app-nativa`, que hoy escriben los mismos campos de `ICategoriaCliente` desde dos lados.
 *
 * El array `categorias` se recibe POR REFERENCIA y se muta in-place (push/splice), así el padre lo
 * ve sin eventos extra. `idCategoriaDefault` y `categoriasDefault` viven en el form raíz y pueden
 * contener ids temporales (`tmp-cat-N`): los resuelve `ClienteFormService.guardar`.
 */
@Component({
  selector: 'app-categorias-seccion',
  templateUrl: './categorias-seccion.component.html',
  styleUrls: ['./categorias-seccion.component.scss'],
  standalone: false,
})
export class CategoriasSeccionComponent {
  private fb = inject(UntypedFormBuilder);
  private matDialog = inject(MatDialog);
  private dialogService = inject(DialogService);
  private helper = inject(HelperService);
  private service = inject(ClienteFormService);

  @Input() formulario!: UntypedFormGroup;
  /** Categorías del cliente, en orden. Se muta in-place. */
  @Input() categorias: CategoriaEditable[] = [];
  /** Catálogo global de categorías (para elegir / duplicar). */
  @Input() categoriasCatalogo: ICategoria[] = [];
  /** Catálogo global de botones (para "usar uno ya creado"). */
  @Input() botonesCatalogo: IBoton[] = [];
  /** idCategoria → nombres de OTROS clientes que la usan. */
  @Input() usoPorCategoria: Record<string, string[]> = {};
  /** idCategoria → cuántos vecinos DE ESTE cliente la tienen, y en qué estado. */
  @Input() usoVecinos: Record<string, IUsoCategoriaEnCliente> = {};
  /**
   * Reemplazos de las categorías que este edición saca del cliente. Se muta in-place, igual que
   * `categorias`, y `ClienteFormService.guardar` lo manda en el PUT resolviendo los ids
   * temporales.
   */
  @Input() reemplazosCategorias: IReemplazoCategoriaCliente[] = [];

  /** Índice de la card expandida (-1 = ninguna). */
  public expandida = -1;

  get nombreCliente(): string {
    return this.formulario?.get('nombre')?.value || '';
  }
  get sirenas(): boolean {
    return !!this.formulario?.get('sirenas')?.value;
  }
  get clienteFormValue(): any {
    return this.formulario?.value ?? {};
  }
  get idCategoriaDefault(): string | null {
    return this.formulario?.get('idCategoriaDefault')?.value ?? null;
  }
  get categoriasDefault(): UntypedFormArray {
    return this.formulario?.get('categoriasDefault') as UntypedFormArray;
  }

  /**
   * Template hecho a mano que le corresponde al cliente, resuelto por bundle ID igual que en el
   * nativo (`Clientes.forBundleId`). Si existe, la botonera no se elige en ninguna categoría y las
   * nuevas arrancan sin botonera propia para no reemplazarlo.
   */
  get templateCustom(): TemplateCustom | undefined {
    return templateCustomDeCliente({
      bundleIdAndroid: this.formulario?.get('bundleIdAndroid')?.value,
      bundleIdIOS: this.formulario?.get('bundleIdIOS')?.value,
    });
  }

  get clienteUsaTemplateCustom(): boolean {
    return !!this.templateCustom;
  }

  /**
   * Botonera genérica guardada en `cliente.inicioTemplate` de un cliente que por bundle ID tiene
   * template hecho a mano. En el nativo el valor del backend le gana al seed
   * (`buildClienteRuntime`), así que eso le está reemplazando la pantalla en TODA la app, no solo
   * en una categoría. Es un dato viejo a limpiar.
   */
  get templateClientePisado(): string | null {
    if (!this.templateCustom) return null;
    const tpl = this.formulario?.get('inicioTemplate')?.value as string | undefined;
    if (!tpl) return null;
    return BOTONERA_SPECS[tpl as BotoneraTemplateId] ? tpl : null;
  }

  /** Limpia `cliente.inicioTemplate` para que vuelva a mandar el template del bundle ID. */
  public limpiarTemplateCliente(): void {
    const control = this.formulario?.get('inicioTemplate');
    control?.patchValue(null);
    control?.markAsDirty();
    this.helper.notifSuccess(
      'Listo. Al guardar, la app vuelve a usar el template del bundle ID.',
    );
  }

  private get porIdBoton(): Map<string, IBoton> {
    return new Map(this.botonesCatalogo.map((b) => [b._id!, b]));
  }

  // ============================ Estado / validación ============================

  get faltaCategoria(): boolean {
    return !this.categorias.length;
  }
  get faltaDefault(): boolean {
    return !!this.categorias.length && !this.idCategoriaDefault;
  }
  /** Rangos por edad mal cargados (desde > hasta) o solapados. */
  get rangosInvalidos(): boolean {
    const filas = (this.categoriasDefault?.value ?? []) as {
      desde: number;
      hasta: number;
    }[];
    const cargadas = filas.filter((f) => f.desde !== null && f.hasta !== null);
    if (cargadas.some((f) => f.desde > f.hasta)) return true;
    const ordenadas = [...cargadas].sort((a, b) => a.desde - b.desde);
    for (let i = 1; i < ordenadas.length; i++) {
      if (ordenadas[i].desde <= ordenadas[i - 1].hasta) return true;
    }
    return false;
  }

  public etiqueta(ed: CategoriaEditable): string {
    return etiquetaEditable(ed);
  }

  public esDefault(ed: CategoriaEditable): boolean {
    return this.idCategoriaDefault === ed.idCategoria;
  }

  /** Chips de los rangos de edad que apuntan a esta categoría. */
  public rangosDe(ed: CategoriaEditable): string[] {
    const filas = (this.categoriasDefault?.value ?? []) as {
      desde: number;
      hasta: number;
      idCategoria: string;
    }[];
    return filas
      .filter((f) => f.idCategoria === ed.idCategoria)
      .map((f) => `${f.desde ?? '?'}-${f.hasta ?? '?'} años`);
  }

  /**
   * Botonera de la card. Sin `inicioTemplate` propio la resuelve el cliente / el bundle ID en la
   * app mobile, que es lo que corresponde en los clientes con template hecho a mano.
   */
  public etiquetaBotonera(ed: CategoriaEditable): string {
    const propia = ed.grupo.get('inicioTemplate')?.value;
    if (propia === ID_PLANTILLA_MANUAL) return 'armada a mano';
    if (propia) return propia;
    return this.templateCustom?.id ?? 'botonera del cliente';
  }

  public tooltipBotonera(ed: CategoriaEditable): string {
    const propia = ed.grupo.get('inicioTemplate')?.value;
    if (propia && this.templateCustom) {
      return `Ojo: reemplaza el template ${this.templateCustom.label} en esta categoría`;
    }
    if (propia === ID_PLANTILLA_MANUAL) {
      return 'Plantilla armada a mano en esta categoría';
    }
    if (propia) return 'Botonera genérica elegida en esta categoría';
    if (this.templateCustom) {
      return `Template hecho a mano en la app: ${this.templateCustom.label}`;
    }
    return 'Sin botonera propia: la define el cliente';
  }

  /** Cantidad de botones por pantalla, para el resumen de la card. */
  public resumenBotones(ed: CategoriaEditable): string {
    return `${ed.idsPantalla1.length} / ${ed.idsPantalla2.length} / ${ed.idsPantalla3.length}`;
  }

  // ============================ Alta / baja / clon ============================

  public async agregar(): Promise<void> {
    const data: DatosAgregarCategoria = {
      categorias: this.categoriasCatalogo,
      usoPorCategoria: this.usoPorCategoria,
      yaUsadas: this.categorias.map((c) => c.idCategoria),
    };
    const ref = this.matDialog.open<
      AgregarCategoriaDialogComponent,
      DatosAgregarCategoria,
      ResultadoAgregarCategoria
    >(AgregarCategoriaDialogComponent, {
      data,
      width: '820px',
      maxWidth: '95vw',
    });
    const res = await firstValueFrom(ref.afterClosed());
    if (!res) return;

    let ed: CategoriaEditable;
    if (res.modo === 'usar') {
      ed = this.service.desdeCliente(
        { idCategoria: res.categoria._id },
        res.categoria,
        this.porIdBoton,
        this.usoPorCategoria[res.categoria._id ?? ''] ?? [],
      );
    } else if (res.modo === 'duplicar') {
      ed = this.service.duplicar(
        res.categoria,
        this.porIdBoton,
        this.nombreCliente,
      );
    } else {
      // La primera categoría de un cliente es casi siempre la "Normal".
      ed = this.service.enBlanco(
        res.preset,
        this.categorias.length ? '' : 'Normal',
        this.clienteUsaTemplateCustom,
      );
    }

    this.categorias.push(ed);
    this.expandida = this.categorias.length - 1;
    // Primera categoría del cliente: pasa a ser la default sin preguntar.
    if (this.categorias.length === 1) {
      this.marcarDefault(ed);
    }
  }

  /** Vecinos de este cliente que tienen la categoría vigente. */
  public vecinosVigentesDe(idCategoria: string): number {
    return this.usoVecinos?.[idCategoria]?.vecinos?.vigentes ?? 0;
  }

  /**
   * Saca la categoría del cliente.
   *
   * Aunque la categoría no se borre, para los vecinos de ESTE cliente el efecto es el mismo que
   * si desapareciera: quedan apuntando a una categoría que su cliente ya no tiene. Por eso, si
   * alguno la tiene vigente, hay que decir a dónde pasa antes de sacarla.
   */
  public async eliminar(i: number): Promise<void> {
    const ed = this.categorias[i];
    const vigentes = this.vecinosVigentesDe(ed.idCategoria);

    if (!vigentes) {
      const ok = await this.dialogService.confirm(
        'Quitar categoría',
        `¿Sacar "${this.etiqueta(ed)}" de este cliente? La categoría en sí no se borra, solo deja de estar asignada.`,
        'Quitar',
      );
      if (!ok) return;
      this.quitar(i, ed, null);
      return;
    }

    const resultado = await this.pedirReemplazo(
      ed,
      vigentes,
      `Quitar "${this.etiqueta(ed)}" de este cliente`,
      'La categoría no se borra, pero estos vecinos dejarían de tenerla. Elegí a qué categoría pasan.',
      'Quitar',
    );
    if (resultado === undefined) return;
    this.quitar(i, ed, resultado);
  }

  /**
   * Abre el diálogo de reemplazo para UNA categoría de este cliente. Devuelve `undefined` si se
   * canceló, o el id elegido (`null` = pasan a usar la categoría por defecto del cliente).
   */
  private async pedirReemplazo(
    ed: CategoriaEditable,
    vigentes: number,
    titulo: string,
    descripcion: string,
    textoConfirmar: string,
    sugerido?: string,
  ): Promise<string | null | undefined> {
    const fila: IFilaReemplazo = {
      clave: ed.idCategoria,
      titulo: this.nombreCliente || 'Este cliente',
      vecinosVigentes: vigentes,
      opciones: this.categorias
        .filter((c) => c.idCategoria !== ed.idCategoria)
        .map((c) => ({
          idCategoria: c.idCategoria,
          nombre: this.etiqueta(c),
        })),
      sugerido,
      nota: this.esDefault(ed) ? 'es la categoría por defecto' : undefined,
    };

    const datos: IDatosReemplazoDialog = {
      titulo,
      descripcion,
      filas: [fila],
      textoConfirmar,
    };

    const resultado: ResultadoReemplazo | undefined = await firstValueFrom(
      this.matDialog
        .open(ReemplazoCategoriaDialogComponent, { width: '650px', data: datos })
        .afterClosed(),
    );
    if (!resultado) return undefined;
    return resultado.get(ed.idCategoria) ?? null;
  }

  /**
   * Saca la categoría del array y anota el reemplazo para que el guardado lo mande al backend.
   * El id puede ser temporal (`tmp-cat-N`) si el reemplazo es una categoría nueva: lo resuelve
   * `ClienteFormService.guardar` junto con el resto de las referencias.
   */
  private quitar(
    i: number,
    ed: CategoriaEditable,
    idCategoriaReemplazo: string | null,
  ): void {
    this.categorias.splice(i, 1);
    this.limpiarReferencias(ed.idCategoria);
    // Una categoría que nunca se guardó no tiene vecinos: no hay nada que reasignar.
    if (!ed.nueva) {
      this.registrarReemplazo(ed.idCategoria, idCategoriaReemplazo);
    }
    if (this.expandida >= this.categorias.length) {
      this.expandida = -1;
    }
  }

  /** Anota (o pisa) el reemplazo de una categoría que sale del cliente. */
  private registrarReemplazo(
    idCategoria: string,
    idCategoriaReemplazo: string | null,
  ): void {
    const existente = this.reemplazosCategorias.find(
      (r) => r.idCategoria === idCategoria,
    );
    if (existente) {
      existente.idCategoriaReemplazo = idCategoriaReemplazo;
      return;
    }
    this.reemplazosCategorias.push({ idCategoria, idCategoriaReemplazo });
  }

  /**
   * Clona una categoría compartida para que este cliente la tenga a solas. La original queda
   * intacta; la referencia del cliente (y los rangos por edad) pasan al clon.
   */
  public async clonar(i: number): Promise<void> {
    const ed = this.categorias[i];
    const ok = await this.dialogService.confirm(
      'Clonar para este cliente',
      `Se crea una copia propia de "${this.etiqueta(ed)}" al guardar. Los otros ${ed.compartidaCon.length} cliente(s) siguen usando la original, sin cambios.`,
      'Clonar',
    );
    if (!ok) return;
    const clon = this.service.clonarEditable(ed, this.nombreCliente);
    const eraDefault = this.esDefault(ed);
    this.categorias.splice(i, 1, clon);
    this.reapuntarReferencias(ed.idCategoria, clon.idCategoria);
    // Para el backend clonar es una quita: la original sale del cliente. Los vecinos que la
    // tenían pasan al clon, que es una copia idéntica — cualquier otra opción sería peor.
    if (!ed.nueva) {
      this.registrarReemplazo(ed.idCategoria, clon.idCategoria);
    }
    if (eraDefault) this.marcarDefault(clon);
    this.expandida = i;
    this.helper.notifSuccess('Copia creada. Se guarda con el cliente.');
  }

  public marcarDefault(ed: CategoriaEditable): void {
    const control = this.formulario?.get('idCategoriaDefault');
    control?.patchValue(ed.idCategoria);
    control?.markAsDirty();
  }

  /** Saca de los rangos por edad (y de la default) las referencias a una categoría eliminada. */
  private limpiarReferencias(idCategoria: string): void {
    if (this.idCategoriaDefault === idCategoria) {
      const control = this.formulario?.get('idCategoriaDefault');
      control?.patchValue(this.categorias[0]?.idCategoria ?? null);
      control?.markAsDirty();
    }
    for (let i = this.categoriasDefault.length - 1; i >= 0; i--) {
      if (
        this.categoriasDefault.at(i).get('idCategoria')?.value === idCategoria
      ) {
        this.categoriasDefault.removeAt(i);
        this.categoriasDefault.markAsDirty();
      }
    }
  }

  /** Reapunta los rangos por edad de una categoría a otra (al clonar). */
  private reapuntarReferencias(idViejo: string, idNuevo: string): void {
    for (const fila of this.categoriasDefault.controls) {
      if (fila.get('idCategoria')?.value === idViejo) {
        fila.patchValue({ idCategoria: idNuevo });
        fila.markAsDirty();
      }
    }
  }

  // ============================ Por edad ============================

  public agregarPorEdad(): void {
    this.categoriasDefault.push(
      this.fb.group({ desde: [null], hasta: [null], idCategoria: [null] }),
    );
    this.categoriasDefault.markAsDirty();
  }

  public eliminarPorEdad(i: number): void {
    this.categoriasDefault.removeAt(i);
    this.categoriasDefault.markAsDirty();
  }

  public toggle(i: number): void {
    this.expandida = this.expandida === i ? -1 : i;
  }
}
